import { parseMoney, roundDecimal } from '@ims/core'
import type { BillDiscountMode } from './cart.ts'

// How a discount box takes its number: the last choice is kept per device, one for
// the lines and one for the bill (docs/plans/discount-modes.md).

export type LineDiscountMode = 'amount' | 'percent'

const KEYS = { line: 'ims.discount-mode.line', bill: 'ims.discount-mode.bill' } as const

export function readDiscountMode(box: 'line'): LineDiscountMode
export function readDiscountMode(box: 'bill'): BillDiscountMode
export function readDiscountMode(box: 'line' | 'bill'): BillDiscountMode {
  try {
    const saved = localStorage.getItem(KEYS[box])
    if (saved === 'percent' || (box === 'bill' && saved === 'final')) return saved
  } catch {
    // Storage off (a private window): rupees, as before.
  }
  return 'amount'
}

export function saveDiscountMode(box: 'line' | 'bill', mode: BillDiscountMode): void {
  try {
    localStorage.setItem(KEYS[box], mode)
  } catch {
    // Storage off: the choice lasts until the page closes.
  }
}

/** A typed percent ("10", "12.5", "10%") in basis points, or null when it isn't one. */
export function parsePercent(text: string): number | null {
  const cleaned = text.trim().replace(/%$/, '').trim()
  if (cleaned === '') return null
  try {
    // Two decimals, like an amount: 12.5 is 1250 basis points as 12.50 is 1250 paisa.
    const bp = parseMoney(roundDecimal(cleaned, 2))
    return bp < 0 ? null : bp
  } catch {
    return null
  }
}

/** Basis points as the box shows them: 1000 → "10", 1250 → "12.5". */
export function percentText(bp: number): string {
  return (bp / 100).toFixed(2).replace(/\.?0+$/, '')
}

/** A typed number that ends in "%": the box switches to percent. */
export function endsWithPercent(text: string): boolean {
  return /%\s*$/.test(text)
}
